import { useTranslation } from "react-i18next";
import DynamicFormInput from "components/form/form";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useEffect, useState, useCallback } from "react";
import {
  getMappedLegalResearchSummaries,
} from "services/legalResearchService";
import { conductLegalResearch } from "types";
import { LoadingIcon } from "utils/constant";
import personImg from "../../images/person.png";
import SingleResearchPaper from "./fragments/singleResearchPapers";
import SingleResearchPapersDetail from "./fragments/singleResearchPapersDetail";
import FileViewer from "components/file/fileViewContainer";
import Pagination from "components/Pagination";

export default function Researches() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeResearch, setResearch] = useState<conductLegalResearch>();
  const [submitting, setSubmitting] = useState(false);
  const { token } = useAuth();
  const { showToast } = useToast();
  const [data, setData] = useState<{
    isLoading: boolean;
    data: conductLegalResearch[];
  }>({ isLoading: true, data: [] });

  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    name: string;
    size: number;
    url: string;
    isFetched: boolean;
    type: string;
    uuid: string;
  } | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 2;

  const fileRowProps = {
    fileViewerNumber: 2,
    setFileViewer2: setIsFileViewerOpen,
    setSelectedApplicantDocument: (document: any) => {
      setSelectedDocument(document);
      setIsFileViewerOpen(true);
    },
  };

  // Filter data based on search term
  const filteredData = useCallback(() => {
   if (!searchTerm.trim()) {
     return data.data || [];
   }

     const searchLower = searchTerm.toLowerCase().trim();
     return (data.data || []).filter((req: conductLegalResearch) => {
       return (
         (req.letter_reference_number || "").toLowerCase().includes(searchLower) ||
         (req.registry_reference_number || "").toLowerCase().includes(searchLower)
       );
     });
   }, [data.data, searchTerm]);

  // Paginate filtered data
  const paginatedData = useCallback(() => {
    const filtered = filteredData();
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filtered.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredData().length / pageSize);

  // Update active research when paginated data changes
  useEffect(() => {
    const currentPaginated = paginatedData();
    if (currentPaginated.length > 0) {
      const currentActiveInPaginated = currentPaginated.find(
        item => item.id === activeResearch?.id
      );

      if (currentActiveInPaginated) {
        setResearch(currentActiveInPaginated);
      } else {
        setResearch(currentPaginated[0]);
      }
    } else {
      setResearch(undefined);
    }
  }, [paginatedData, activeResearch?.id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleSearch = async () => {
    setSubmitting(true);
    try {
      // Frontend search is handled by filteredData
    } catch (error) {
      console.error("Search error:", error);
      showToast(t("SearchFailed"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Enter key for search
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

   useEffect(() => {
    const fetchData = async () => {
      try {
        setData({ isLoading: true, data: [] });
        const combined = await getMappedLegalResearchSummaries();

        const publishedOnly = combined.filter((item: any) => item.is_published === true);

        setData({ isLoading: false, data: publishedOnly });

        if (publishedOnly.length > 0) {
          setResearch(publishedOnly[0]);
        }
        setCurrentPage(1);
      } catch (error) {
        console.error("Error fetching legal research requests:", error);
        showToast(t("FailedToFetchLegalResearchRequest"), "error");
        setData((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
  }, [token, showToast, t]);

  // Calculate filtered and paginated results
  const currentFilteredResults = filteredData();
  const currentPaginatedResults = paginatedData();
  const hasFilteredResults = currentFilteredResults.length > 0;

  return (
    <div className="flex flex-col md:flex-row h-full gap-4">
      <div className="flex flex-col flex-1 overflow-y-hidden">
        <div className="flex flex-col bg-white rounded-lg shadow-md p-4 md:p-6 mb-4">
          <p className="mb-2 font-medium">{t("research.search_title")}</p>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="w-full">
              <div onKeyDown={handleKeyDown}>
                <DynamicFormInput
                  inputs={[
                    {
                      value: searchTerm,
                      type: "text",
                      label: t("research.search_label"),
                      name: "search",
                      onChange: handleChange,
                      placeholder: t("research.search_placeholder"),
                    },
                  ]}
                />
              </div>
            </div>
            <button
              onClick={handleSearch}
              disabled={submitting}
              className="bg-[#212121] hover:bg-[#141414] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-2 px-12 rounded-md h-fit w-full md:w-fit"
            >
              {submitting ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon} {t("research.searching")}
                </div>
              ) : (
                t("research.search_button")
              )}
            </button>
          </div>
          {searchTerm && (
            <div className="mt-2 text-sm text-gray-600">
              {t("research.results_count", { count: currentFilteredResults.length, term: searchTerm })}
            </div>
          )}
        </div>

        {data.isLoading ? (
          <div className="text-center mb-auto text-gray-500 py-8">
            <div className="flex justify-center mb-2">
              {LoadingIcon}
            </div>
            <p>{t("research.loading_message")}</p>
          </div>
        ) : !hasFilteredResults ? (
          <div className="text-center mb-auto text-gray-500 py-8">
            <p>
              {searchTerm.trim()
                ? t("research.no_results_found", { term: searchTerm })
                : t("research.no_data_yet")}
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4 overflow-y-scroll scrollbar-hide flex-1">
              {currentPaginatedResults.map((research: conductLegalResearch) => (
                <SingleResearchPaper
                  key={research.id}
                  personImg={personImg}
                  research={research}
                  isActive={research.id === activeResearch?.id}
                  onClick={() => setResearch(research)}
                />
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>

      {/* Detail Panel */}
      <div className="flex flex-col flex-1 h-full overflow-scroll scrollbar-hide">
        {hasFilteredResults && activeResearch ? (
          <SingleResearchPapersDetail
            personImg={personImg}
            research={activeResearch}
            fileRowProps={fileRowProps}
            onUpdate={(updatedResearch) => {
              setResearch(updatedResearch);
              setData((prev) => ({
                ...prev,
                data: prev.data.map((r: conductLegalResearch) =>
                  r.id === updatedResearch.id ? updatedResearch : r
                ),
              }));
            }}
          />
        ) : hasFilteredResults && !activeResearch ? (
          <div className="text-center p-8 text-gray-500">
            <p>{t("SelectResearchPaperToViewDetails")}</p>
          </div>
        ) : null}
      </div>
      <FileViewer
        selectedApplicantDocument={selectedDocument || undefined}
        isPopupOpen={isFileViewerOpen}
        setIsPopupOpen={setIsFileViewerOpen}
      />
    </div>
  );
}